import { MarketoConfig } from '../types/project-metadata.types';

declare const initWebchat;
declare const Munchkin;
declare const rtp;
declare const et_seg2;

/**
 * Loads an external script by adding it to the `document.head` of the application.
 *
 *
 * **Attention: Keep in mind, that the parameter `useCMP` does not prevent the script from loading! It just adds
 * the attributes in the way the consent manager does this.**
 *
 * @param useCMP if true or undefined, use consent manager script attributes instead of normal html ones
 * @param src src of the script
 * @param vendorId vendor id from consent-manager.net
 * @param onLoad function that will be executed after the src-library has been downloaded parsed and processed by the browser
 */
export const loadExternalScript = (
    useCMP: boolean | undefined,
    src: string,
    vendorId?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onLoad?: (this: GlobalEventHandlers, ev: Event) => any
) => {
    const script = document.createElement('script');
    if (useCMP !== false && vendorId !== undefined) {
        script.type = 'text/plain';
        script.setAttribute('data-cmp-vendor', vendorId);
        script.setAttribute('data-cmp-src', src);
        script.classList.add('cmplazyload');
    } else {
        script.type = 'text/javascript';
        script.src = src;
    }
    script.async = true;
    if (onLoad !== undefined) {
        script.onload = onLoad;
    }
    document.head.appendChild(script);
};

/**
 * Loads the cognigy webchat widget.
 *
 * @param useCMP flag for the usage of the consent manager
 * @param endpointUrl endpoint url used as an input for the script function initWebchat(<endpointUrl>)
 */
export const loadCognigyWebchatWidget = (
    useCMP: boolean | undefined,
    endpointUrl: string,
    scriptSrc: string
) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function scriptCognigyInitWebChat(this: GlobalEventHandlers, _ev: Event) {
        initWebchat(endpointUrl);
    }
    loadExternalScript(useCMP, scriptSrc, 'c55844', scriptCognigyInitWebChat);
};

/**
 * Loads the Microsoft Clarity script
 *
 * @param useCMP flag for the usage of the consent manager
 * @param code clarity code
 */
export const loadClarityScript = (useCMP: boolean | undefined, clarityCode: string) => {
    const clarityUrl = 'https://www.clarity.ms/tag/' + clarityCode;
    window['clarity'] = function (...args) {
        (window['clarity'].q = window['clarity'].q || []).push(args);
    };
    loadExternalScript(useCMP, clarityUrl, 's2631');
};

/**
 * Loads the marketo munchkin webchat widget.
 *
 * @param useCMP flag for the usage of the consent manager
 */
export const loadMarketoMunchkin = (useCMP: boolean | undefined, config: MarketoConfig) => {
    let appName = null;
    // for the wrapper service we have to track the page visit manually, because otherwise it
    // is not possible to differentiate between the different apps
    if (window.location.pathname === '/os/c/main.do') {
        // take the app name from the etracker cc values
        appName = decodeURI(et_seg2);
        config.properties.apiOnly = true;
    }
    loadExternalScript(
        useCMP,
        'https://munchkin.marketo.net/munchkin.js',
        's9',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        function (this: GlobalEventHandlers, _ev: Event) {
            Munchkin.init(config.accountId, config.properties);
            if (appName) {
                Munchkin.munchkinFunction('visitWebPage', {
                    url: '/os/c/main.do',
                    params: 'app=' + appName,
                });
            }
        }
    );
    if (config.personalizationActivated) {
        window['rtp'] = function (...args) {
            (window['rtp'].q = window['rtp'].q || []).push(args);
        };
        loadExternalScript(
            useCMP,
            'https://lonrtp1-cdn.marketo.com/rtp-api/v1/rtp.js?aid=sew',
            's9',
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            function (this: GlobalEventHandlers, _ev: Event) {
                rtp('send', 'view');
                rtp('get', 'campaign', true);
            }
        );
    }
};
